









































































import { Component, Watch } from 'vue-property-decorator';
import { mixins } from 'vue-class-component';
import { Validation, validationMixin } from 'vuelidate';
import { Validations } from 'vuelidate-property-decorators';
import { AxiosError, AxiosResponse } from 'axios';

import {
	formatPaymentMethod,
	hasAccountFields,
	PaymentType,
	PaymentMethodCreateParams,
	PaymentMethodType,
	paymentMethodError,
} from '@/models/org-payment-method';
import { accountFieldValidators } from '@/common/Validators';
import { OrdwayService } from '@/services/ordway-service';
import Toast from '@/components/display/toast.vue';

@Component({
	methods: {
		paymentMethodError,
	},
})
export default class AddPaymentMethod extends mixins(validationMixin) {
	get paymentOptions(): Array<{
		value: PaymentType;
		text: string;
	}> {
		return this.unsetPaymentTypes.map((paymentMethodType: PaymentMethodType) => ({
			value: paymentMethodType.type,
			text: formatPaymentMethod(paymentMethodType.type),
		}));
	}

	get shouldShowAccountFields(): boolean {
		if (this.newPaymentMethod !== null) {
			return hasAccountFields(this.newPaymentMethod.type as PaymentType);
		}
		return false;
	}

	public get canSubmit(): boolean {
		if (this.$v.newPaymentMethod !== undefined) {
			const { $invalid } = this.$v.newPaymentMethod;
			return !$invalid;
		}
		return false;
	}

	public newPaymentMethod: PaymentMethodCreateParams | null = null;
	public submitting: boolean = false;
	private unsetPaymentTypes: PaymentMethodType[] = [];
	@Validations()
	public validations() {
		if (this.newPaymentMethod !== null) {
			return {
				newPaymentMethod: accountFieldValidators(this.newPaymentMethod.type),
			};
		}
		return {};
	}
	@Watch('newPaymentMethod.active')
	public ensurePaymentMethodApproved(paymentMethodActive: boolean): void {
		if (paymentMethodActive && this.newPaymentMethod !== null) {
			this.newPaymentMethod.approved = true;
		}
	}

	public created() {
		this.fetchUnsetPaymentMethods();
	}

	public fieldWithValidator(fieldName: string): Validation {
		return this.$v.newPaymentMethod[fieldName] as Validation;
	}

	public onPaymentMethodChanged(newPaymentMethodType: PaymentType): void {
		if (this.newPaymentMethod) {
			this.newPaymentMethod.billToOrgId = this.unsetPaymentTypes.find(
				(paymentMethod: PaymentMethodType) => paymentMethod.type === newPaymentMethodType,
			)!.billToOrgId;
			if (newPaymentMethodType === 'directInvoice') {
				// Approved by default.
				this.newPaymentMethod.approved = true;
				this.newPaymentMethod.accountName = '';
				this.newPaymentMethod.accountNumber = '';
			} else {
				this.newPaymentMethod.approved = false;
			}
		}
	}

	public async onSubmit(): Promise<void> {
		// We have passed validation if we are here.
		this.submitting = true;
		if (this.newPaymentMethod !== null && this.newPaymentMethod.active === true) {
			this.$bvModal.show('confirm-active');
		} else {
			await this.saveChanges();
		}
	}

	public async saveChanges(): Promise<void> {
		if (this.newPaymentMethod !== null) {
			await this.addNewPaymentMethod(this.newPaymentMethod);
			this.$emit('success');
			this.closeModal();
		}
	}

	public closeModal(): void {
		this.$bvModal.hide('addPaymentMethod');
	}

	private fetchUnsetPaymentMethods() {
		const orgId: number = parseInt(this.$route.params.orgId, 10);
		OrdwayService.fetchAdditionalPaymentMethodTypes(orgId).then(
			(paymentMethodTypes: AxiosResponse<{ data: PaymentMethodType[] }>) => {
				this.unsetPaymentTypes = paymentMethodTypes.data.data;
				const defaultPaymentMethod = this.unsetPaymentTypes[0];
				this.newPaymentMethod = {
					orgId,
					type: defaultPaymentMethod.type,
					accountName: '',
					accountNumber: '',
					approved: defaultPaymentMethod.type === 'directInvoice',
					active: false,
					billToOrgId: defaultPaymentMethod.billToOrgId,
				};
			},
		);
	}

	private async addNewPaymentMethod(
		newPaymentMethodParams: PaymentMethodCreateParams,
	): Promise<void> {
		try {
			await OrdwayService.addPaymentMethod(newPaymentMethodParams);
			new Toast().makeToast('Payment method created successfully', 'Success', 'success');
		} catch (e) {
			const error = e as AxiosError;
			this.handleError(error);
		} finally {
			this.submitting = false;
		}
	}

	private handleError(error: AxiosError): void {
		let errorMessage: string;
		if (error.response) {
			errorMessage = error.response.data.errors;
		} else {
			errorMessage =
				'There was a problem updating the payment method - changes have not been applied.';
		}
		new Toast().makeToast(errorMessage, 'Error updating payment method', 'danger');
	}
}
